import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../../../context/AuthContext";
import {
  OrganizationMember,
  userService,
  UserRole,
  MemberStatus,
} from "../../../../services/UserService";
import InviteMembersModal from "./InviteMembersModal";
import { useToastContext } from "../../../../context/ToastContext";
import { useLoader } from "../../../../context/LoaderContext";

import {
  Trash,
  SendHorizontal,
  UserPen,
  Clock,
  XCircle,
  Copy,
  User,
  ArrowLeftRight,
  Building,
  UserX,
  Circle,
  UserPlusIcon,
} from "lucide-react";
import ConfirmRemoveModal from "../../modals/ConfirmRemoveModal";
import ResendInvitationModal from "../../modals/ResendInvitationModal";
import UpdateRoleModal from "../../modals/UpdateRoleModal";
import { generateRandomColor } from "../../../../utils/RandomColorGenerator";
import { InvitedUser } from "../../../../models/UserModels";
import { formatRole } from "../../../../utils/RoleUtils";
import TransferOwnershipModal from "../../modals/TransferOwnershipModal";
import OrganizationDropdown from "./OrganizationDropDown";
import { organizationService } from "../../../../services/OrganizationService";
import LeaveOrganizationModal from "../../modals/LeaveOrganizationModal";
import ActivateMemberModal from "../../modals/ActivateMemberModal";
import InlineEditOrganizationName from "./InlineEditOrganizationName";

const UserManager: React.FC = () => {
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const { userProfile, organizationOverview } = useAuth();
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isActivateMemberModalOpen, setIsActivateMemberModalOpen] =
    useState(false);
  const [isLeaveOrganizationModalOpen, setIsLeaveOrganizationModalOpen] =
    useState(false);
  const [selectedMemberEmail, setSelectedMemberEmail] = useState<string>("");
  const [selectedMemberId, setSelectedMemberId] = useState<string>("");
  const [isUpdateRoleModalOpen, setIsUpdateRoleModalOpen] = useState(false);
  const [_, setIsLoadingMembers] = useState(false);
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const [selectedMemberRole, setSelectedMemberRole] = useState<UserRole>(
    UserRole.Member
  );
  const [isTransferOwnershipModalOpen, setIsTransferOwnershipModalOpen] =
    useState(false);

  const { showToast } = useToastContext();
  const { showLoader, hideLoader } = useLoader();
  const { login, dataRefresh } = useAuth();

  const currentUserRole =
    userProfile?.organizations?.find(
      (org) =>
        org.organizationId === organizationOverview?.currentOrganization.id
    )?.role || UserRole.Member;
  const currentUserStatus =
    userProfile?.organizations?.find(
      (org) =>
        org.organizationId === organizationOverview?.currentOrganization.id
    )?.status || MemberStatus.Pending;
  const isOwner = currentUserRole === UserRole.Owner;
  const isAdmin = currentUserRole === UserRole.Admin;
  const isSuperAdmin = currentUserRole === UserRole.SuperAdmin;
  const isMember = currentUserRole === UserRole.Member;

  // Aligns with backend
  const isPrivilegedAdmin =
    currentUserRole === UserRole.SuperAdmin ||
    currentUserRole === UserRole.Owner;

  const activeMembers = members.filter(
    (member) => member.status === MemberStatus.Active || member.pending
  );

  const inactiveMembers = members.filter(
    (member) => member.status === MemberStatus.Inactive
  );

  const confirmLeaveOrganization = async () => {
    showLoader();

    setIsLeaveOrganizationModalOpen(false);

    try {
      const response = await userService.leaveOrganization();
      showToast(
        "success",
        response.message || "You have successfully left the organization."
      );

      await dataRefresh();
      await fetchAllUsersPerOrganization();
    } catch (error: any) {
      console.error("Error leaving organization:", error);
      showToast("error", error.message || "Failed to leave the organization.");
    } finally {
      hideLoader();
    }
  };

  const confirmActivateMember = async () => {
    setIsActivateMemberModalOpen(false);
    showLoader();

    try {
      const { message } = await userService.activateMember(selectedMemberId);

      showToast("success", message);
      await dataRefresh();
      await fetchAllUsersPerOrganization();
    } catch (error: any) {
      const errorMessage = error?.message || "Failed to activate user.";
      showToast("error", errorMessage);
    } finally {
      hideLoader();
    }
  };

  const confirmRemoveMember = async () => {
    setIsRemoveModalOpen(false);
    showLoader();

    console.log("User id from remove from main func", selectedMemberId);

    try {
      const { message } = await userService.removeMember(selectedMemberId);

      showToast("success", message);
      await dataRefresh();
      await fetchAllUsersPerOrganization();
    } catch (error: any) {
      const errorMessage = error?.message || "Failed to remove user.";
      showToast("error", errorMessage);
    } finally {
      hideLoader();
    }
  };

  const handleInviteMembers = async (invitedUsers: InvitedUser[]) => {
    showLoader();
    setIsLoadingMembers(true);
    try {
      const emails = invitedUsers.map((user) => user.email);
      const role = invitedUsers[0]?.role || UserRole.Member;

      const response = await userService.inviteUsersToOrganization(
        emails,
        role as UserRole
      );

      response.results.forEach((result) => {
        if (result.status === "success") {
          showToast("success", `Invitation sent to ${result.email}`);
        } else {
          showToast("error", `${result.email}: ${result.message}`);
        }
      });

      await fetchAllUsersPerOrganization();
    } catch (error: any) {
      showToast("error", error.message || "Failed to send invitations.");
    } finally {
      hideLoader();
      setIsLoadingMembers(false);
    }
  };

  const fetchAllUsersPerOrganization = useCallback(async () => {
    try {
      const response = await userService.getOrganizationMembers();
      setMembers(response);
    } catch (error) {
      console.error("Failed to fetch members by organization:", error);
    } finally {
      console.log("Members fetched successfully");
    }
  }, []);

  useEffect(() => {
    fetchAllUsersPerOrganization();
  }, [fetchAllUsersPerOrganization]);

  const handleResendInvitation = async () => {
    setIsResendModalOpen(false);
    showLoader();
    try {
      const { message } = await userService.resendInvitation(
        selectedMemberEmail
      );
      showToast("success", message);
    } catch (error: any) {
      console.error("Error resending invitation:", error);
      showToast("error", error?.message || "Failed to resend invitation.");
    } finally {
      hideLoader();
    }
  };

  const handleUpdateRole = async (userId: string, newRole: UserRole) => {
    setIsUpdateRoleModalOpen(false);
    showLoader();
    try {
      const { message } = await userService.updateUserRole(userId, newRole);

      showToast("success", message);
      await fetchAllUsersPerOrganization();
    } catch (error: any) {
      console.error("Error updating user role:", error);
      showToast("error", error?.message || "Failed to update user role.");
    } finally {
      hideLoader();
    }
  };

  const calculateTimeRemaining = (expiresAt: string | null): string => {
    if (!expiresAt) return "No expiration date";

    const expirationDate = new Date(expiresAt);
    const now = new Date();
    const timeDiff = expirationDate.getTime() - now.getTime();
    const daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    return daysRemaining > 0
      ? `${daysRemaining} day${daysRemaining > 1 ? "s" : ""} remaining`
      : "Expired";
  };

  const handleTransferOwnership = async (newOwnerId: string) => {
    console.log("New owner id: ", newOwnerId);
    setIsTransferOwnershipModalOpen(false);
    showLoader();

    try {
      const { message } = await userService.transferOwnership(newOwnerId);

      showToast("success", message);

      await dataRefresh();
      await fetchAllUsersPerOrganization();

      console.log("Ownership transfer completed, data refreshed.");
    } catch (error: any) {
      console.error("Failed to transfer ownership:", error);
      showToast(
        "error",
        error?.response?.data?.message ||
          "Failed to transfer ownership. Please try again."
      );
    } finally {
      hideLoader();
    }
  };

  const handleUpdateOrganizationName = async (newName: string) => {
    showLoader();

    try {
      const { message } = await organizationService.updateOrganizationName(
        newName
      );

      showToast("success", message);
      await dataRefresh();
    } catch (error: any) {
      showToast(
        "error",
        error?.response?.data?.message ||
          "Failed to update the organization name. Please try again."
      );
    } finally {
      hideLoader();
    }
  };

  const handleSwitchOrganization = async (orgId: string) => {
    if (!orgId) {
      return;
    }
    showLoader();
    try {
      const { message } = await organizationService.switchOrganizations(orgId);
      console.log("Message from switch organization: ", message);
      login();
      await fetchAllUsersPerOrganization();
      console.log("Login successful");
      showToast("success", message || "Organization switched successfully!");
    } catch (error: any) {
      showToast(
        "error",
        error.message || "Organization switch failed. Please try again."
      );
    } finally {
      hideLoader();
    }
  };

  const handleToggleOrgDropdown = () => {
    setIsOrgDropdownOpen((prev) => !prev);
  };

  const openResendModal = (email: string) => {
    setSelectedMemberEmail(email);
    setIsResendModalOpen(true);
  };

  const handleRemoveMember = (member: OrganizationMember) => {
    setSelectedMemberEmail(member.email);
    setSelectedMemberId(member.userId);
    setIsRemoveModalOpen(true);
  };

  const handleActivateMember = (member: OrganizationMember) => {
    setSelectedMemberEmail(member.email);
    setSelectedMemberId(member.userId);
    setIsActivateMemberModalOpen(true);
  };

  const handleLeaveOrganization = (member: OrganizationMember) => {
    setSelectedMemberEmail(member.email);
    setSelectedMemberId(member.userId);
    setIsLeaveOrganizationModalOpen(true);
  };

  const handleTransferOwnershipModal = (member: OrganizationMember) => {
    setSelectedMemberEmail(member.email);
    setSelectedMemberId(member.userId);
    setIsTransferOwnershipModalOpen(true);
  };

  const handleOpenInviteModal = () => {
    setIsInviteModalOpen(true);
  };

  const handleCloseInviteModal = () => {
    setIsInviteModalOpen(false);
  };

  const handleCopyOrganizationId = () => {
    const organizationId = organizationOverview?.currentOrganization.id;
    if (!organizationId) return;
    navigator.clipboard.writeText(organizationId);
    showToast("success", "Organization ID copied!");
  };

  return (
    <div className="min-h-screen bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark flex flex-col">
      <div className="bg-background-default dark:bg-background-dark">
        <div className="px-6 py-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-semibold text-text-primary dark:text-text-primaryDark mb-1">
                Organization Management
              </h1>
              <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
                Manage team members and organization settings
              </p>
            </div>

            <div className="relative">
              <button
                onClick={handleToggleOrgDropdown}
                className="flex items-center gap-2 py-2 px-4 bg-background-default dark:bg-background-panelDark text-sm font-medium rounded-md border border-divider dark:border-divider-dark hover:bg-background-panel dark:hover:bg-background-panelDark transition"
              >
                <Building className="w-4 h-4 text-primary dark:text-text-primaryDark" />

                <span className="truncate">
                  {organizationOverview?.currentOrganization.name ||
                    "Select Organization"}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 px-6 pb-6">
        <div className="min-h-40 bg-background-paper dark:bg-background-paperDark p-6 rounded-lg border border-divider dark:border-divider-dark mb-6">
          <InlineEditOrganizationName
            isEditable={isPrivilegedAdmin}
            memberCount={activeMembers.length}
            onSave={(newName) => handleUpdateOrganizationName(newName)}
          />
          <p className="text-base text-text-secondary dark:text-text-secondaryDark flex flex-wrap items-center gap-2">
            <span>Organization ID:</span>
            <span className="font-semibold text-secondary dark:text-secondaryDark break-all">
              {organizationOverview?.currentOrganization.id}
            </span>
            <button
              onClick={handleCopyOrganizationId}
              className="p-1 rounded-md border border-divider dark:border-white/20 hover:bg-background-paper dark:hover:bg-[#3a3a3a] transition flex-shrink-0"
            >
              <Copy className="w-4 h-4 text-text-secondary dark:text-white/70" />
            </button>
          </p>
        </div>

        <div className="overflow-x-auto bg-background-paper dark:bg-background-paperDark rounded-lg border border-divider dark:border-divider-dark mb-6 shadow-none">
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="border-b border-divider dark:border-divider-dark">
                <th className="px-6 py-3 text-left text-sm font-semibold text-text-secondary dark:text-text-secondaryDark uppercase tracking-wide">
                  Avatar
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-text-secondary dark:text-text-secondaryDark uppercase tracking-wide">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-text-secondary dark:text-text-secondaryDark uppercase tracking-wide">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-text-secondary dark:text-text-secondaryDark uppercase tracking-wide">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-text-secondary dark:text-text-secondaryDark uppercase tracking-wide">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-text-secondary dark:text-text-secondaryDark uppercase tracking-wide">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {activeMembers.map((member, index) => {
                console.log("Member status: ", member.status);
                const rowStyle =
                  index % 2 === 0
                    ? "bg-background-default dark:bg-background-dark"
                    : "bg-background-paper dark:bg-background-panelDark/50";

                return (
                  <tr
                    key={member.userId}
                    className={`${rowStyle} border-b border-divider dark:border-divider-dark dark:hover:bg-secondary/10 transition`}
                  >
                    <td className="px-6 py-4 text-sm whitespace-nowrap">
                      {member.avatar ? (
                        <img
                          src={member.avatar}
                          alt={`${member.firstName || "User"}'s Avatar`}
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{
                            backgroundColor: generateRandomColor(member.email),
                          }}
                        >
                          <span className="text-xs text-white">
                            {member.firstName?.charAt(0) || "U"}
                            {member.lastName?.charAt(0) || ""}
                          </span>
                        </div>
                      )}
                    </td>

                    <td className="px-6 py-4 text-sm whitespace-nowrap">
                      {member.firstName || "Pending Registration"}{" "}
                      {member.lastName || ""}
                    </td>

                    <td className="px-6 py-4 text-sm whitespace-nowrap">
                      {member.email}
                    </td>

                    <td className="px-6 py-4 text-sm whitespace-nowrap">
                      {formatRole(member.role)}
                    </td>

                    <td className="px-6 py-4 text-sm whitespace-nowrap">
                      {member.pending ? (
                        calculateTimeRemaining(member.expiresAt) ===
                        "Expired" ? (
                          <div className="flex items-center text-red-500">
                            <XCircle className="w-4 h-4 mr-1" />
                            Expired
                          </div>
                        ) : (
                          <div className="flex items-center text-secondary">
                            <Clock className="w-4 h-4 mr-2" />
                            {calculateTimeRemaining(member.expiresAt)}
                          </div>
                        )
                      ) : (
                        <span className="text-green-600 font-semibold">
                          {formatRole("Active")}
                        </span>
                      )}
                    </td>

                    <td className="px-6 py-4 text-right">
                      {isMember &&
                        member.role === UserRole.Owner &&
                        member.userId !== userProfile?._id && (
                          <span className="text-sm text-gray-500 italic">
                            View only
                          </span>
                        )}

                      <div
                        className="flex items-center justify-end gap-x-2 overflow-hidden whitespace-nowrap"
                        style={{ overflowX: "hidden" }}
                      >
                        {member.pending &&
                          userProfile?._id !== member.userId &&
                          currentUserStatus === MemberStatus.Active && (
                            <button
                              onClick={() => openResendModal(member.email)}
                              className="px-3 py-1 h-8 text-xs border-secondary text-secondary rounded-md hover:bg-secondary hover:text-white transition"
                            >
                              <SendHorizontal className="w-4 h-4" />
                            </button>
                          )}

                        {!member.pending &&
                          member.role !== UserRole.Owner &&
                          member.userId !== userProfile?._id &&
                          member.status === MemberStatus.Active &&
                          (currentUserRole === UserRole.Owner ||
                            currentUserRole === UserRole.Admin ||
                            currentUserRole === UserRole.SuperAdmin) && (
                            <button
                              onClick={() => {
                                setSelectedMemberId(member.userId);
                                setSelectedMemberEmail(member.email);
                                setSelectedMemberRole(member.role as UserRole);
                                setIsUpdateRoleModalOpen(true);
                              }}
                              className="px-3 py-1 h-8 text-xs border-secondary text-secondary rounded-md hover:bg-secondary hover:text-white transition"
                            >
                              <UserPen className="w-4 h-4" />
                            </button>
                          )}

                        {currentUserRole === UserRole.Owner &&
                          !member.pending &&
                          member.role !== UserRole.Owner &&
                          member.status === MemberStatus.Active &&
                          member.userId !== userProfile?._id && (
                            <button
                              onClick={() =>
                                handleTransferOwnershipModal(member)
                              }
                              className="px-3 py-1 h-8 text-xs border-secondary text-secondary rounded-md hover:bg-secondary hover:text-white transition"
                            >
                              <ArrowLeftRight className="w-4 h-4" />
                            </button>
                          )}

                        {!member.pending &&
                          member.role !== UserRole.Owner &&
                          member.status === MemberStatus.Active &&
                          member.userId === userProfile?._id && (
                            <button
                              onClick={() => handleLeaveOrganization(member)}
                              className="px-3 py-1 h-8 text-xs border-red-500 text-red-500 rounded-md hover:bg-red-500 hover:text-white transition"
                            >
                              <UserX className="w-4 h-4" />
                            </button>
                          )}

                        {currentUserRole === UserRole.Owner &&
                          member.userId !== userProfile?._id &&
                          member.role !== UserRole.Owner && (
                            <button
                              onClick={() => handleRemoveMember(member)}
                              className="px-3 py-1 h-8 text-xs border-red-500 text-red-500 rounded-md hover:bg-red-500 hover:text-white transition"
                            >
                              <Trash className="w-4 h-4" />
                            </button>
                          )}

                        {member.userId === userProfile?._id && (
                          <button
                            onClick={() => {}}
                            className="px-3 py-1 h-8 text-xs border-secondary text-secondary rounded-md"
                          >
                            <Circle className="w-2 h-2 bg-green-dark text-green-dark rounded" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}

              {inactiveMembers.length > 0 && (
                <>
                  <tr className="bg-background-panel dark:bg-background-paperDark">
                    <td
                      colSpan={6}
                      className="text-sm font-bold px-6 py-4 text-text-primary dark:text-text-primaryDark"
                    >
                      Inactive Members
                    </td>
                  </tr>

                  {inactiveMembers.map((member, index) => {
                    const rowStyle =
                      index % 2 === 0
                        ? "bg-background-default dark:bg-background-dark"
                        : "bg-background-paper dark:bg-background-panelDark/50";

                    return (
                      <tr
                        key={member.userId}
                        className={`${rowStyle} border-b border-divider dark:border-divider-dark dark:hover:bg-secondary/20 transition`}
                      >
                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                          {member.avatar ? (
                            <img
                              src={member.avatar}
                              alt={`${member.firstName || "User"}'s Avatar`}
                              className="w-8 h-8 rounded-full"
                            />
                          ) : (
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center"
                              style={{
                                backgroundColor: generateRandomColor(
                                  member.email
                                ),
                              }}
                            >
                              <span className="text-xs text-white">
                                {member.firstName?.charAt(0) || "U"}
                                {member.lastName?.charAt(0) || ""}
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                          {member.firstName || "Unknown"}{" "}
                          {member.lastName || ""}
                        </td>
                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                          {member.email}
                        </td>
                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                          {formatRole(member.role)}
                        </td>
                        <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500 font-semibold">
                          Inactive
                        </td>
                        <td className="px-6 py-4 text-right">
                          {/* Member needs to be active */}
                          {(currentUserRole === UserRole.Owner ||
                            currentUserRole === UserRole.Admin ||
                            (currentUserRole === UserRole.SuperAdmin &&
                              currentUserStatus === MemberStatus.Active)) && (
                            <button
                              onClick={() => handleActivateMember(member)}
                              className="px-3 py-1 h-8 text-xs border-secondary text-secondary rounded-md hover:bg-secondary hover:text-white transition"
                            >
                              <UserPlusIcon className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </>
              )}
              {members.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-8 text-center text-sm text-text-secondary dark:text-text-secondaryDark"
                  >
                    Loading members...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Subtle Footer Spacing */}
        <div className="mt-12 pt-8 border-t border-divider/30 dark:border-divider-dark/30">
          <div className="flex items-center justify-center gap-2 text-xs text-text-secondary/50 dark:text-text-secondaryDark/50">
            <span>Forgeworks</span>
            <span>•</span>
            <span>v1.0.0</span>
          </div>
        </div>
      </div>

      {(isOwner || isAdmin || isSuperAdmin) && (
        <button
          onClick={handleOpenInviteModal}
          className="fixed bottom-6 right-6 bg-secondary text-white w-14 h-14 flex items-center justify-center rounded-full shadow-lg hover:bg-secondary-dark focus:outline-none focus:ring-4 focus:ring-secondary focus:ring-opacity-50 transition"
        >
          <User className="w-6 h-6" />
        </button>
      )}
      {isInviteModalOpen && (
        <InviteMembersModal
          open={isInviteModalOpen}
          onClose={handleCloseInviteModal}
          onProceed={handleInviteMembers}
        />
      )}
      {isRemoveModalOpen && (
        <ConfirmRemoveModal
          open={isRemoveModalOpen}
          onClose={() => setIsRemoveModalOpen(false)}
          onConfirm={confirmRemoveMember}
          email={selectedMemberEmail}
          organizationName={
            organizationOverview?.currentOrganization.name ||
            "this organization"
          }
        />
      )}

      {isResendModalOpen && (
        <ResendInvitationModal
          open={isResendModalOpen}
          onClose={() => setIsResendModalOpen(false)}
          onConfirm={handleResendInvitation}
          email={selectedMemberEmail}
          organizationName={
            organizationOverview?.currentOrganization.name || ""
          }
          currentRole={selectedMemberRole}
        />
      )}

      {isUpdateRoleModalOpen && (
        <UpdateRoleModal
          open={isUpdateRoleModalOpen}
          onClose={() => setIsUpdateRoleModalOpen(false)}
          onConfirm={(selectedRole) =>
            handleUpdateRole(selectedMemberId, selectedRole)
          }
          email={selectedMemberEmail}
          currentRole={selectedMemberRole}
          organizationName={
            organizationOverview?.currentOrganization.name || ""
          }
          memberCount={members.length}
          currentUserRole={
            userProfile?.organizations?.find(
              (org) =>
                org.organizationId ===
                organizationOverview?.currentOrganization.id
            )?.role || UserRole.Member
          }
        />
      )}
      {isTransferOwnershipModalOpen && (
        <TransferOwnershipModal
          open={isTransferOwnershipModalOpen}
          onClose={() => setIsTransferOwnershipModalOpen(false)}
          onConfirm={(newOwnerId) => handleTransferOwnership(newOwnerId)}
          currentOwnerEmail={userProfile?.email || ""}
          organizationName={
            organizationOverview?.currentOrganization.name ||
            "Your Organization"
          }
          selectedMemberEmail={selectedMemberEmail}
          selectedMemberId={selectedMemberId}
        />
      )}

      {isLeaveOrganizationModalOpen && (
        <LeaveOrganizationModal
          open={isLeaveOrganizationModalOpen}
          onClose={() => setIsLeaveOrganizationModalOpen(false)}
          onConfirm={confirmLeaveOrganization}
          organizationName={
            organizationOverview?.currentOrganization.name ||
            "Your Organization"
          }
        />
      )}

      {isActivateMemberModalOpen && (
        <ActivateMemberModal
          open={isActivateMemberModalOpen}
          onClose={() => setIsActivateMemberModalOpen(false)}
          onConfirm={confirmActivateMember}
          email={selectedMemberEmail}
          organizationName={
            organizationOverview?.currentOrganization.name ||
            "this organization"
          }
        />
      )}

      <OrganizationDropdown
        isOpen={isOrgDropdownOpen}
        onClose={() => setIsOrgDropdownOpen(false)}
        organizationOverview={organizationOverview}
        handleSwitchOrganization={handleSwitchOrganization}
      />
    </div>
  );
};

export default UserManager;
